import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import './Dashboard.css';

const API_URL = process.env.REACT_APP_API_URL || '';

// 格式化答案：识别编号、加粗、分段（律师风格）
function formatAnswer(text) {
  if (!text) return null;
  const lines = (text || '').split('\n');
  const elements = [];
  let key = 0;
  let sceneCount = 0;
  let inAdvice = false;
  let adviceItems = [];

  const flushAdvice = () => {
    if (adviceItems.length === 0) return;
    elements.push(
      <div key={key++} style={{
        background: '#f0f7ff',
        borderLeft: '3px solid #1a73e8',
        borderRadius: 8,
        padding: '12px 16px',
        margin: '12px 0'
      }}>
        <div style={{ fontWeight: 600, color: '#1a73e8', marginBottom: 8, fontSize: 14 }}>
          💡 实务建议
        </div>
        {adviceItems.map((item, si) => (
          <div key={si} style={{
            paddingLeft: 16,
            marginBottom: 6,
            lineHeight: 1.6,
            fontSize: 14,
            color: '#333',
            position: 'relative'
          }}>
            <span style={{
              position: 'absolute',
              left: 0,
              top: 0,
              color: '#1a73e8',
              fontWeight: 600
            }}>{si + 1}.</span>
            <span>{item}</span>
          </div>
        ))}
      </div>
    );
    adviceItems = [];
    inAdvice = false;
  };

  for (const line of lines) {
    const trimmed = (line || '').trim();
    if (!trimmed) {
      elements.push(<div key={key++} style={{ height: 8 }} />);
      continue;
    }

    // 检测"实务建议"开头
    if (trimmed.startsWith('实务建议')) {
      flushAdvice();
      inAdvice = true;
      const firstLine = trimmed.replace(/^实务建议[：:]\s*/g, '').replace(/（[^）]*）/g, '').trim();
      if (firstLine) adviceItems.push(firstLine);
      continue;
    }

    // 如果已在实务建议块中，收集内容
    if (inAdvice) {
      if (trimmed.startsWith('免责') || trimmed.startsWith('以上回答') || trimmed.startsWith('⚠️') || trimmed.startsWith('📚')) {
        flushAdvice();
        // 不 continue，让当前行继续走下面的逻辑
      } else {
        const cleaned = trimmed.replace(/（[^）]*）/g, '').trim();
        if (cleaned) adviceItems.push(cleaned);
        continue;
      }
    }

    // 场景分隔：遇到新的"场景:"时添加分隔线
    if (trimmed.startsWith('场景:') || trimmed.startsWith('场景：') || trimmed.startsWith('场景 ')) {
      flushAdvice();
      sceneCount++;
      if (sceneCount > 1) {
        elements.push(
          <div key={key++} style={{
            borderTop: '1px dashed #ccc',
            margin: '16px 0',
            height: 1
          }} />
        );
      }
      elements.push(
        <div key={key++} style={{ fontWeight: 600, color: '#1a73e8', marginBottom: 4 }}>
          {formatInline(trimmed)}
        </div>
      );
      continue;
    }

    const numMatch = trimmed.match(/^(\d+)[\.、）\)]\s*/);
    if (numMatch && numMatch[1] && !trimmed.startsWith('根据') && !trimmed.startsWith('综上')) {
      const rest = trimmed.replace(/^(\d+)[\.、）\)]\s*/, '');
      elements.push(
        <div key={key++} className="qa-answer-step">
          <span className="qa-step-num">{numMatch[1]}</span>
          <span className="qa-step-text">{formatInline(rest)}</span>
        </div>
      );
    } else if (trimmed.startsWith('注意') || trimmed.startsWith('提示') || trimmed.startsWith('免责') || trimmed.startsWith('以上回答')) {
      flushAdvice();
      elements.push(
        <div key={key++} className="qa-answer-note">
          ⚠️ {formatInline(trimmed.replace(/^(注意[：:]?|提示[：:]?|免责[：:]?|以上回答[：:]?)\s*/, ''))}
        </div>
      );
    } else if (trimmed.startsWith('根据') || trimmed.startsWith('依据') || trimmed.startsWith('按照')) {
      flushAdvice();
      elements.push(
        <div key={key++} className="qa-answer-reference">
          📋 {formatInline(trimmed)}
        </div>
      );
    } else if (trimmed.includes('《') && trimmed.includes('》') && (trimmed.includes('条') || trimmed.includes('款') || trimmed.includes('项'))) {
      flushAdvice();
      elements.push(
        <div key={key++} className="qa-answer-law">
          ⚖️ {formatInline(trimmed)}
        </div>
      );
    } else {
      elements.push(
        <div key={key++} className="qa-answer-line">{formatInline(trimmed)}</div>
      );
    }
  }

  // 如果最后还有实务建议没刷新
  flushAdvice();

  return elements;
}

function formatInline(text) {
  const safeText = text || '';
  const parts = safeText.split(/(\*\*[^*]+\*\*)/g);
  return parts.map((part, i) => {
    if (part.startsWith('**') && part.endsWith('**')) {
      return <strong key={i}>{part.slice(2, -2)}</strong>;
    }
    return part;
  });
}

export default function LegalQA({ embedded }) {
  const navigate = useNavigate();
  const [question, setQuestion] = useState('');
  const [answer, setAnswer] = useState('');
  const [sources, setSources] = useState([]);
  const [sourceType, setSourceType] = useState('local');
  const [sourceLabel, setSourceLabel] = useState('');
  const [disclaimer, setDisclaimer] = useState('');
  const [loading, setLoading] = useState(false);
  const [history, setHistory] = useState([]);

  useEffect(() => {
    const saved = localStorage.getItem('legal_qa_history');
    if (saved) {
      try { setHistory(JSON.parse(saved)); } catch {}
    }
  }, []);

  const handleAsk = async () => {
    if (!question.trim()) return;
    
    setLoading(true);
    setAnswer('');
    setSources([]);
    setSourceType('local');
    setSourceLabel('');
    setDisclaimer('');
    
    try {
      const resp = await fetch(`${API_URL}/api/knowledge/qa`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ question: question.trim(), type: 'legal' })
      });
      const data = await resp.json();
      
      if (data.success) {
        setAnswer(data.answer);
        setSources(data.sources || []);
        setSourceType(data.sourceType || 'local');
        setSourceLabel(data.sourceLabel || '');
        setDisclaimer(data.disclaimer || '');
        
        const newHistory = [{ q: question.trim(), a: data.answer, time: new Date().toISOString() }, ...history.slice(0, 19)];
        setHistory(newHistory);
        localStorage.setItem('legal_qa_history', JSON.stringify(newHistory));

        const currentUser = localStorage.getItem('notes_user') || '';
        fetch(`${API_URL}/api/knowledge/qa/log`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ question: question.trim(), user: currentUser, type: 'legal' })
        }).catch(() => {});
      } else {
        setAnswer(data.error || '抱歉，无法回答这个问题。请换个方式提问。');
      }
    } catch (e) {
      setAnswer('网络错误，请稍后重试。');
    } finally {
      setLoading(false);
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleAsk();
    }
  };

  const clearHistory = () => {
    setHistory([]);
    localStorage.removeItem('legal_qa_history');
  };

  return (
    <div className="knowledge-container">
      {/* 非嵌入模式：显示返回按钮和标题 */}
      {!embedded && (
        <div className="kb-browse-header">
          <button className="kb-back-btn" onClick={() => navigate('/knowledge')}>← 返回</button>
          <h2>⚖️ 法务助手</h2>
        </div>
      )}

      {/* 提示区域：两种模式都显示 */}
      <div className="qa-intro">
        <p>基于法律法规+司法解释+案例知识库，以专业律师口吻提供法律咨询和分析</p>
        <div className="qa-sources-list">
          <span className="qa-source-tag">📚 法务知识库（法律法规+司法解释+案例）</span>
          <span className="qa-source-tag">⚖️ 专业律师口吻</span>
          <span className="qa-source-tag">🤖 AI知识兜底</span>
        </div>
      </div>

      <div className="qa-input-box">
        <textarea
          value={question}
          onChange={(e) => setQuestion(e.target.value)}
          onKeyDown={handleKeyDown}
          placeholder="输入法律问题，如：刑法第三十条的规定是什么？公司法定代表人越权担保的合同效力如何认定？"
          className="qa-input"
          rows={3}
          autoFocus
        />
        <button 
          className="qa-submit-btn" 
          onClick={handleAsk}
          disabled={loading || !question.trim()}
        >
          {loading ? '分析中...' : '提问'}
        </button>
      </div>

      {loading && (
        <div className="qa-loading">
          <div className="knowledge-spinner" />
          <p>正在检索法律法规并分析...</p>
        </div>
      )}

      {answer && !loading && (
        <div className="qa-answer-box">
          <div className="qa-answer-header">
            <span className="qa-answer-icon">⚖️</span>
            <span>法律意见</span>
            <span className={`qa-answer-badge ${sourceType === 'ai' ? 'qa-badge-ai' : ''}`}>
              {sourceLabel || (sourceType === 'ai' ? '🤖 AI知识' : '📚 法务知识库')}
            </span>
          </div>
          
          <div className="qa-answer-content">
            {formatAnswer(answer)}
          </div>
          
          {disclaimer && (
            <div className="qa-disclaimer" style={{ marginBottom: 16 }}>
              ⚠️ {disclaimer}
            </div>
          )}
          
          {sources.length > 0 && (
            <div className="qa-sources">
              <div className="qa-sources-header">
                <span>📚 法律依据</span>
                <span className="qa-sources-count">{Math.min(sources.length, 5)}条相关</span>
              </div>
              <div className="qa-sources-list-detail">
                {sources.slice(0, 5).map((s, idx) => (
                  <div key={idx} className="qa-source-item">
                    <div className="qa-source-num">#{idx + 1}</div>
                    <div className="qa-source-content">
                      <div className="qa-source-q">{s.q}</div>
                      <div className="qa-source-a">
                        {s.a ? (s.a.length > 200 ? s.a.substring(0, 200) + '...' : s.a) : '无答案'}
                      </div>
                      <div className="qa-source-meta">
                        <span className="qa-source-from">📂 {s.source}</span>
                        {s.related_laws && s.related_laws.length > 0 && (
                          <span className="qa-tag">关联法条: {s.related_laws.join(', ')}</span>
                        )}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      )}

      {history.length > 0 && !loading && (
        <div className="qa-history">
          <div className="qa-history-header">
            <span>📝 最近提问</span>
            <button className="qa-clear-btn" onClick={clearHistory}>清空</button>
          </div>
          <div className="qa-history-list">
            {history.slice(0, 5).map((h, idx) => (
              <div key={idx} className="qa-history-item" onClick={() => {
                setQuestion(h.q);
                setAnswer('');
                setSources([]);
              }}>
                <span className="qa-history-q">{h.q}</span>
                <span className="qa-history-time">{new Date(h.time).toLocaleDateString('zh-CN')}</span>
              </div>
            ))}
          </div>
        </div>
      )}

      <div className="qa-suggestions">
        <h4>试试这些法律问题：</h4>
        <div className="qa-suggestion-list">
          <span className="qa-hint" onClick={() => { setQuestion('刑法第三十条的规定是什么？'); handleAsk(); }}>刑法第三十条</span>
          <span className="qa-hint" onClick={() => { setQuestion('公司法定代表人越权担保的合同效力如何认定？'); handleAsk(); }}>法定代表人越权担保</span>
          <span className="qa-hint" onClick={() => { setQuestion('盗窃罪与诈骗罪的区别是什么？'); handleAsk(); }}>盗窃vs诈骗</span>
          <span className="qa-hint" onClick={() => { setQuestion('民事诉讼时效期间是多久？'); handleAsk(); }}>民事诉讼时效</span>
        </div>
      </div>
    </div>
  );
}
